import React from "react";
import { useSelector } from "react-redux";

import { AdvancedCareActivityViewOnlyForm } from "@/components/Provider/PatientView/MemberProfile/AdvancedCareActivity/AdvancedCareActivityViewOnlyForm";
import { CareManagementForm } from "@/components/Provider/PatientView/MemberProfile/CareManagement/CareManagementForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { Divider2 } from "@/ui2/atoms/Divider";
import { Heading2 } from "@/ui2/atoms/Heading";
import type { NoteDetail, PatientDetails } from "@/core/types";
import type { RootState } from "@/lib/types/state";
import { Spinner2 } from "@/ui2/atoms/Spinner";
import { testLocator } from "@/lib/helpers/test-locator";

import { noteV1Display } from "../helpers/noteV1Display";
import { noteV2Display } from "../helpers/noteV2Display";
import { noteV3Display } from "../helpers/noteV3Display";
import { noteV4Display } from "../helpers/noteV4Display";

import { CareCommunicationView } from "./components/CareCommunicationView";
import { CaseManagementView } from "./components/CaseManagementView";
import { SignatureList } from "./components/SignatureList";

import "./notes.print.styles.scss";

type Props = {
  isEditing: boolean;
  note: NoteDetail;
  patient: PatientDetails | undefined;
  patientName: string;
  refetchNote: () => void;
  setIsEditing: (v: boolean) => void;
};

export function GenericNoteView({
  isEditing,
  note,
  patient,
  patientName,
  refetchNote,
  setIsEditing,
}: Props) {
  const profileName = useSelector<RootState, string | null | undefined>(
    (state) => state.auth.profile?.name,
  );

  return (
    <NoteContainer note={note} patient={patient} patientName={patientName}>
      <NoteContent
        isEditing={isEditing}
        note={note}
        patientName={patientName}
        profileName={profileName}
        refetchNote={refetchNote}
        setIsEditing={setIsEditing}
      />
    </NoteContainer>
  );
}

type NoteContainerProps = {
  children: React.ReactNode;
  note: NoteDetail;
  patient: PatientDetails | undefined;
  patientName: string;
};

const NoteContainer = ({ children, note, patient, patientName }: NoteContainerProps) => {
  if (!note) return <Spinner2 className="tw:mx-auto tw:mt-30" />;

  if (note?.note_type === "Care Management" || note?.note_type === "Advanced Care Activity") {
    return <>{children}</>;
  }

  return (
    <DashboardCard2 as="article" className="tw:flex tw:flex-col tw:gap-6" rawChildren>
      <header className="tw:flex tw:flex-col tw:gap-1" {...testLocator("note-header")}>
        <p className="" data-private>
          <strong>{renderPatientMetaData(note, patient, patientName)}</strong>
        </p>

        <Heading2 as="h1" size={3}>
          {renderNoteTitle(note)}
        </Heading2>
      </header>

      <Divider2 />

      {children}

      <Divider2 />
      <SignatureList note={note} />
    </DashboardCard2>
  );
};

type NoteContentProps = {
  isEditing: boolean;
  note: NoteDetail;
  patientName: string;
  profileName: string | null | undefined;
  refetchNote: () => void;
  setIsEditing: (v: boolean) => void;
};

function NoteContent({
  isEditing,
  note,
  patientName,
  profileName,
  refetchNote,
  setIsEditing,
}: NoteContentProps) {
  if (note?.note_type === "Care Management") {
    return (
      <CareManagementForm
        isEditing={isEditing}
        nameToSign={note.provider_name ?? profileName}
        noteState={{ ...note, signature: note.signature ?? "" }}
        patientId={note.patient_id}
        refetchNote={refetchNote}
        setIsEditing={setIsEditing}
      />
    );
  }

  if (note?.note_type === "Advanced Care Activity") {
    return <AdvancedCareActivityViewOnlyForm note={note} />;
  }

  switch (note.version ?? 1) {
    case 1:
      return noteV1Display(note, patientName);

    case 2:
      return noteV2Display(note, false, null, { profileView: true });

    case 3:
      return noteV3Display(note);

    case 4:
      return noteV4Display(note);

    case 5:
      return note?.note_type !== "Discharge" ? <CareCommunicationView note={note} /> : null;

    case 6:
      return <CaseManagementView note={note} />;

    default:
      return null;
  }
}

const renderNoteTitle = (note: NoteDetail): string | undefined => {
  if (!note || note.title === "Care Communication Note" || note.note_type === "Care Management") {
    return undefined;
  }

  return note.title;
};

const renderPatientMetaData = (
  note: NoteDetail,
  patient: PatientDetails | undefined,
  patientName: string,
): React.ReactNode => {
  if (!patient || !note?.note_type || note?.note_type === "Care Management") {
    return undefined;
  }

  return (
    <>
      {patientName} (Date of Birth : {patient.dob})
    </>
  );
};
